import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Stack,
    Tooltip,
    Slider,
    InputAdornment,
} from '@mui/material';
import { Point, IconType, GlobalContext } from '../../../context/context';
import { handlePointStyleEdit } from '../handlers/pointStyleHandlers';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface PointStyleModalProps {
    open: boolean;
    onClose: () => void;
    point: Point | null;
    global: GlobalContext;
    editLayer: (updatedLayer: any, layerId: number) => void;
    setSelectedPoint: (point: Point | null) => void;
    setIsPointModalOpen: (open: boolean) => void;
}

interface IconOption {
    icon: React.ReactElement;
    value: IconType;
    labelKey: string;
}

const iconOptions: IconOption[] = [
    { icon: <Icon size={20} name="location-dot" />, value: 'default', labelKey: 'default' },
    { icon: <Icon size={20} name="star" />, value: 'star', labelKey: 'star' },
    { icon: <Icon size={20} name="location-dot" />, value: 'location', labelKey: 'location' },
    { icon: <Icon size={20} name="flag" />, value: 'flag', labelKey: 'flag' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.LocalHospitalOutlined} />, value: 'hospital', labelKey: 'hospital' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.SchoolOutlined} />, value: 'school', labelKey: 'school' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.ShoppingCartOutlined} />, value: 'shopping', labelKey: 'shopping' },
    { icon: <Icon size={20} name="plane" />, value: 'airport', labelKey: 'airport' },
    { icon: <Icon size={20} name="bus" />, value: 'bus', labelKey: 'bus' },
    { icon: <Icon size={20} name="train" />, value: 'train', labelKey: 'train' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.StoreOutlined} />, value: 'store', labelKey: 'store' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.RestaurantOutlined} />, value: 'restaurant', labelKey: 'restaurant' },
    { icon: <Icon size={20} name="square-parking" />, value: 'parking', labelKey: 'parking' }
];

const colorPalette = [
    '#FF0000', // Red
    '#FF4500', // Orange Red
    '#FFA500', // Orange
    '#FFD700', // Gold
    '#FFFF00', // Yellow
    '#32CD32', // Lime Green
    '#008000', // Green
    '#00CED1', // Dark Turquoise
    '#0000FF', // Blue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#FF1493', // Deep Pink
    '#A52A2A', // Brown
    '#000000', // Black
    '#808080', // Gray
    '#FFFFFF', // White
];

export default function PointStyleModal({ 
    open, 
    onClose, 
    point, 
    global,
    editLayer,
    setSelectedPoint,
    setIsPointModalOpen
}: PointStyleModalProps) {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const [editedPoint, setEditedPoint] = React.useState<Point | null>(null);

    React.useEffect(() => {
        if (point) {
            setEditedPoint({
                ...point,
            });
        }
    }, [point]);

    if (!editedPoint) return null;

    const handleSave = () => {
        if (editedPoint) {
            handlePointStyleEdit({
                point: editedPoint,
                global,
                editLayer,
                setSelectedPoint,
                setIsPointModalOpen
            });
        }
    };

    const handleSizeChange = (newSize: number) => {
        if (newSize >= 12 && newSize <= 64) { // Enforce size limits
            setEditedPoint({ ...editedPoint, size: newSize });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('mapDialogs:pointStyle.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        label={t('mapDialogs:pointStyle.pointName')}
                        value={editedPoint.name}
                        onChange={(e) => setEditedPoint({ ...editedPoint, name: e.target.value })}
                        fullWidth
                    />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:pointStyle.coordinates')}
                        </Typography>
                        <Typography>
                            {t('mapDialogs:pointStyle.latitude', { value: editedPoint.coordinates[0].toFixed(6) })}
                        </Typography>
                        <Typography>
                            {t('mapDialogs:pointStyle.longitude', { value: editedPoint.coordinates[1].toFixed(6) })}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:pointStyle.iconSize')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={editedPoint.size || 24}
                                onChange={(_, value) => handleSizeChange(value as number)}
                                min={12}
                                max={64}
                                step={1}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                value={editedPoint.size || 24}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        handleSizeChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                }}
                                sx={{ width: 100 }}
                                size="small"
                            />
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:pointStyle.iconStyle')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {iconOptions.map((option) => (
                                <Tooltip key={option.value} title={t(`mapDialogs:pointStyle.icon.${option.labelKey}`)}>
                                    <IconButton
                                        onClick={() => setEditedPoint({ ...editedPoint, icon: option.value })}
                                        sx={{
                                            border: editedPoint.icon === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                            color: editedPoint.colour,
                                            width: 40,
                                            height: 40,
                                        }}
                                    >
                                        {option.icon}
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:pointStyle.color')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {colorPalette.map((color) => (
                                <IconButton
                                    key={color}
                                    onClick={() => setEditedPoint({ ...editedPoint, colour: color })}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: color,
                                        border: editedPoint.colour === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                        '&:hover': {
                                            backgroundColor: color,
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom>
                                {t('mapDialogs:pointStyle.customColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: editedPoint.colour,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={editedPoint.colour}
                                    onChange={(e) => setEditedPoint({ ...editedPoint, colour: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {t('mapDialogs:common.saveChanges')}
                </Button>
            </DialogActions>
        </Dialog>
    );
} 